import { useCallback, useState } from 'react'
import { showNotification } from '@mantine/notifications'
import { api, ApiError } from '@/lib/api'

type ReplyFavoritesState = {
  template_ids: string[]
  macro_ids: string[]
}

const EMPTY_REPLY_FAVORITES: ReplyFavoritesState = {
  template_ids: [],
  macro_ids: [],
}

export function useTicketReplyTools(projectId?: string | null) {
  const [replyFavorites, setReplyFavorites] = useState<ReplyFavoritesState>(EMPTY_REPLY_FAVORITES)
  const [varsOpen, setVarsOpen] = useState(false)
  const [varsLoading, setVarsLoading] = useState(false)
  const [varsMap, setVarsMap] = useState<Record<string, string>>({})
  const [favoritesOpen, setFavoritesOpen] = useState(false)
  const [favoriteTemplateDraft, setFavoriteTemplateDraft] = useState<string[]>([])
  const [favoriteMacroDraft, setFavoriteMacroDraft] = useState<string[]>([])
  const [favoritesSaving, setFavoritesSaving] = useState(false)

  const loadReplyFavorites = useCallback((nextProjectId: string) => {
    return api
      .replyFavoritesGet(nextProjectId)
      .then(setReplyFavorites)
      .catch(() => setReplyFavorites(EMPTY_REPLY_FAVORITES))
  }, [])

  const openVariablesModal = useCallback(() => {
    setVarsOpen(true)
    setVarsLoading(true)
    void api
      .templateVariables()
      .then(setVarsMap)
      .catch(() => setVarsMap({}))
      .finally(() => setVarsLoading(false))
  }, [])

  const openFavoritesModal = useCallback(() => {
    setFavoriteTemplateDraft([...(replyFavorites.template_ids || [])])
    setFavoriteMacroDraft([...(replyFavorites.macro_ids || [])])
    setFavoritesOpen(true)
  }, [replyFavorites.macro_ids, replyFavorites.template_ids])

  const saveReplyFavorites = useCallback(async () => {
    if (!projectId) return
    setFavoritesSaving(true)
    try {
      const response = await api.replyFavoritesPut({
        project_id: projectId,
        template_ids: favoriteTemplateDraft.slice(0, 5),
        macro_ids: favoriteMacroDraft.slice(0, 5),
      })
      setReplyFavorites(response)
      setFavoritesOpen(false)
      showNotification({
        title: 'Сохранено',
        message: 'Частые шаблоны и макросы обновлены.',
        color: 'green',
      })
    } catch (requestError) {
      showNotification({
        title: 'Ошибка',
        message: requestError instanceof ApiError ? requestError.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setFavoritesSaving(false)
    }
  }, [favoriteMacroDraft, favoriteTemplateDraft, projectId])

  const toggleFavoriteTemplate = useCallback((id: string) => {
    setFavoriteTemplateDraft((prev) => {
      if (prev.includes(id)) return prev.filter((candidate) => candidate !== id)
      if (prev.length >= 5) {
        showNotification({
          title: 'Лимит',
          message: 'Не больше 5 шаблонов в избранном.',
          color: 'yellow',
        })
        return prev
      }
      return [...prev, id]
    })
  }, [])

  const toggleFavoriteMacro = useCallback((id: string) => {
    setFavoriteMacroDraft((prev) => {
      if (prev.includes(id)) return prev.filter((candidate) => candidate !== id)
      if (prev.length >= 5) {
        showNotification({
          title: 'Лимит',
          message: 'Не больше 5 макросов в избранном.',
          color: 'yellow',
        })
        return prev
      }
      return [...prev, id]
    })
  }, [])

  return {
    loadReplyFavorites,
    openFavoritesModal,
    openVariablesModal,
    replyFavorites,
    replyToolsModals: {
      favoritesOpened: favoritesOpen,
      favoritesSaving,
      favoriteMacroIds: favoriteMacroDraft,
      favoriteTemplateIds: favoriteTemplateDraft,
      onCloseFavorites: () => setFavoritesOpen(false),
      onCloseVars: () => setVarsOpen(false),
      onSaveFavorites: saveReplyFavorites,
      onToggleMacro: toggleFavoriteMacro,
      onToggleTemplate: toggleFavoriteTemplate,
      varsLoading,
      varsMap,
      varsOpened: varsOpen,
    },
  }
}
